/* Chic and Bling - collection page interactions.
   Progressive enhancement: if this file fails to load, the page renders as
   stock Dawn plus the static CSS. Nothing here is required to browse or buy.

   Three jobs:
   1. Hero parallax - cursor and scroll drive CSS variables; the CSS does the
      rest, moving three depths at different rates.
   2. Staggered grid reveal (IntersectionObserver).
   3. Card tilt toward the pointer (fine pointers only).

   Everything is rAF-throttled and writes only CSS custom properties, so the
   browser stays on the compositor and never re-lays-out during motion.

   The hero layers deliberately carry NO css transition while tracking - a
   transition would restart every frame during scroll and lag behind the page.
   The .cb-settle class is added on pointerleave so they ease home instead of
   snapping, then removed as soon as tracking resumes. */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var FINE = window.matchMedia('(hover: hover) and (pointer: fine)');

  var MAX_TILT = 5.5;
  var STAGGER = 55;
  var MAX_STAGGER = 6;

  function grid() { return document.getElementById('product-grid'); }

  /* ---------- 1. hero parallax: cursor + scroll ---------- */
  function initHeroParallax() {
    if (REDUCED.matches) return;

    var hero = document.querySelector('.collection-hero');
    if (!hero || hero.hasAttribute('data-cb-hero')) return;
    hero.setAttribute('data-cb-hero', '');

    /* --- cursor --- */
    if (FINE.matches) {
      var pFrame = null;
      var rect = null;
      var settleTimer = null;

      hero.addEventListener('pointerenter', function () {
        clearTimeout(settleTimer);
        hero.classList.remove('cb-settle');
        rect = hero.getBoundingClientRect();
      });

      hero.addEventListener('pointermove', function (e) {
        if (pFrame) return;
        pFrame = requestAnimationFrame(function () {
          pFrame = null;
          if (!rect) rect = hero.getBoundingClientRect();
          var px = (e.clientX - rect.left) / rect.width - 0.5;
          var py = (e.clientY - rect.top) / rect.height - 0.5;
          hero.style.setProperty('--cb-px', px.toFixed(3));
          hero.style.setProperty('--cb-py', py.toFixed(3));
        });
      });

      hero.addEventListener('pointerleave', function () {
        if (pFrame) { cancelAnimationFrame(pFrame); pFrame = null; }
        rect = null;
        hero.classList.add('cb-settle');
        hero.style.setProperty('--cb-px', '0');
        hero.style.setProperty('--cb-py', '0');
        // drop the easing again once the settle has finished, so scrolling
        // afterwards is not damped
        clearTimeout(settleTimer);
        settleTimer = setTimeout(function () {
          hero.classList.remove('cb-settle');
        }, 550);
      });
    }

    /* --- scroll ---
       -1 .. 1 as the hero travels up past the top of the viewport. */
    var sFrame = null;
    function onScroll() {
      if (sFrame) return;
      sFrame = requestAnimationFrame(function () {
        sFrame = null;
        var r = hero.getBoundingClientRect();
        var h = r.height || 1;
        var p = -r.top / h;
        if (p < -1) p = -1;
        if (p > 1) p = 1;
        hero.style.setProperty('--cb-sy', p.toFixed(3));
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- 2. staggered reveal ---------- */
  function initReveal(root) {
    if (REDUCED.matches || !('IntersectionObserver' in window)) return;

    var items = root.querySelectorAll('.grid__item:not([data-cb-seen])');
    if (!items.length) return;

    root.classList.add('cb-ready');

    var batch = 0;
    var flushTimer = null;

    var io = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          el.style.setProperty('--cb-d', Math.min(batch, MAX_STAGGER) * STAGGER + 'ms');
          batch++;
          clearTimeout(flushTimer);
          flushTimer = setTimeout(function () { batch = 0; }, 180);
          el.classList.add('cb-in');
          obs.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    items.forEach(function (el) {
      el.setAttribute('data-cb-seen', '');
      io.observe(el);
    });
  }

  /* ---------- 3. card tilt ---------- */
  function initTilt(root) {
    if (REDUCED.matches || !FINE.matches) return;

    var wrappers = root.querySelectorAll('.card-wrapper:not([data-cb-tilt])');

    wrappers.forEach(function (wrap) {
      wrap.setAttribute('data-cb-tilt', '');

      var frame = null;
      var rect = null;

      wrap.addEventListener('pointerenter', function () {
        rect = wrap.getBoundingClientRect();
      });

      wrap.addEventListener('pointermove', function (e) {
        if (!rect) rect = wrap.getBoundingClientRect();
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = null;
          var px = (e.clientX - rect.left) / rect.width - 0.5;
          var py = (e.clientY - rect.top) / rect.height - 0.5;
          wrap.style.setProperty('--cb-ry', (px * MAX_TILT * 2).toFixed(2) + 'deg');
          wrap.style.setProperty('--cb-rx', (-py * MAX_TILT * 2).toFixed(2) + 'deg');
        });
      });

      wrap.addEventListener('pointerleave', function () {
        if (frame) { cancelAnimationFrame(frame); frame = null; }
        rect = null;
        wrap.style.setProperty('--cb-ry', '0deg');
        wrap.style.setProperty('--cb-rx', '0deg');
      });
    });
  }

  function init() {
    initHeroParallax();
    var root = grid();
    if (!root) return;
    initReveal(root);
    initTilt(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Dawn swaps #product-grid's contents on filter/sort/paginate without a page
     load, which would leave the new cards unbound. Watch for it. */
  if ('MutationObserver' in window) {
    var host = document.querySelector('.collection') || document.body;
    var pending = null;
    new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(init, 60);
    }).observe(host, { childList: true, subtree: true });
  }
})();
